import { useEffect, useState } from 'react';

import { CommonCloudDedicatedProps, CommonStepProps, UrlInfo } from '../shared';

type PickedValues = {
  sdl: CommonStepProps['values']['sdl'];
  cloudRegion: CommonStepProps['values']['cloudRegion'];
  unregisteredSubgraphName: CommonStepProps['values']['unregisteredSubgraphName'];
  unregisteredEndpointUrl: CommonStepProps['values']['unregisteredEndpointUrl'];
};

/**
 * Default API path used when an `inputUrl` is not provided.
 * @constant
 */
export const DEFAULT_API_PATH = '/api/graphql';

/**
 * Checks if a resource's region code matches the given cloud region.
 * @function
 * @param {Object} resource - The resource to check.
 * @param {Object} resource.region - The region data of the resource.
 * @param {string} resource.region.code - The region code of the resource.
 * @param {string|null} cloudRegion - The cloud region to match against.
 * @returns {boolean} True if the resource's region code matches the cloud region, or if no cloud region is provided.
 */
export const isSameRegion = (resource: UrlInfo, cloudRegion: string | null) =>
  !cloudRegion || resource.region.code === cloudRegion;

/**
 * Composes a URL using a base and a path.
 * @function
 * @param {string} protocol - The protocol to use. Defaults to https.
 * @param {string} baseUrl - The base URL.
 * @param {string} path - The path to append to the base URL.
 * @returns {string} The composed URL.
 */
export const composeUrl = ({
  protocol = 'https',
  base,
  path,
}: {
  protocol: string;
  base: string;
  path: string;
}) => `${protocol}://${base}${path}`;

/**
 * Determines if the provided sdl and subgraphName have sufficient data.
 * @function
 * @param {string} sdl - The sdl value.
 * @param {string} subgraphName - The subgraph name value.
 * @returns {boolean} True if both sdl and subgraphName contain more than one character, false otherwise.
 */
export const hasSufficientData = (sdl: string, subgraphName: string) =>
  sdl.length > 1 && subgraphName.length > 1;

/**
 * Custom React hook that manages the logic for composing a URL based on input data.
 * The composed URL is derived either from a previously selected URL from past steps or from a list of available resources.
 * It prioritizes the previously selected URL, if available, and falls back to the first resource's domain URL.
 *
 * @function
 * @param {Object} values - The set of values containing relevant data.
 * @param {string} values.sdl - The sdl value.
 * @param {string} values.unregisteredSubgraphName - The unregistered subgraph name value.
 * @param {string} values.unregisteredEndpointUrl - The previously selected or inputted URL.
 * @param {string|null} values.cloudRegion - The selected cloud region.
 * @param {Array} availableUnregisteredResources - A list of available unregistered resources.
 * @param {function} setFieldValue - A callback function to update a field value.
 * @returns {Object} Contains the composed URL, selected URL, input URL, and their respective setter functions.
 */
export const useComposedUrl = ({
  values,
  availableUnregisteredResources,
  selectedPrivateDomainName,
}: { values: PickedValues; availableUnregisteredResources: UrlInfo[] } & Pick<
  CommonCloudDedicatedProps,
  'availableUnregisteredResources' | 'selectedPrivateDomainName'
>) => {
  const filteredResources: UrlInfo[] = availableUnregisteredResources.filter(
    (resource: UrlInfo) => isSameRegion(resource, values.cloudRegion),
  );

  const [composedUrl, setComposedUrl] = useState<string>('');
  const [inputUrl, setInputUrl] = useState<string | null>(null);

  useEffect(() => {
    const protocol = 'https';
    // The logic below relies on a slight quirk that the logic for this SELECT that sets the value of `selectedPrivateDomainName` to an empty string
    // when the user has not yet selected a privateDomainName. This perhaps should be reorganized, but for an immediate
    // production fix, I'm just going to update this logic to match the author's intent.
    const base =
      selectedPrivateDomainName === ''
        ? filteredResources[0]?.domainUrl
        : selectedPrivateDomainName;
    const path = inputUrl ?? DEFAULT_API_PATH;

    if (base) {
      setComposedUrl(composeUrl({ protocol, base, path }));
    }
  }, [filteredResources, inputUrl, selectedPrivateDomainName]);

  return {
    composedUrl,
    inputUrl,
    setInputUrl,
  };
};
